import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchSingleLot } from "../../../redux/actions/immobilierActions";
import mainAxios from "../../../redux/api/mainAxios";
import toast from "react-hot-toast";
import { closeModal } from "../../../redux/actions/modalActions";
import { uploadAndCreateImageEntry } from "../../../utils/createImgforLots";
import { imageLoader } from "../../../utils/imageLoader";
import TextHtmlEditor from "./TextHtmlEditor";

const EditLotModal = ({ lot }) => {
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadedImageEntries, setUploadedImageEntries] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    reference: lot.reference || "",
    price_total: lot.price_total || "",
    rent: lot.rent || "",
    rent_profitability: lot.rent_profitability || "",
    commission: lot.commission || "",
    description: lot.description || "",
    price_excl_vat: lot.price_excl_vat || "",
    price_incl_tax: lot.price_incl_tax || "",
    price_vat_rate: lot.price_vat_rate || "",
    price_m2: lot.price_m2 || "",
    parking_price_excl_vat: lot.parking_price_excl_vat || "",
    parking_price_incl_tax: lot.parking_price_incl_tax || "",
    parking_price_vat_rate: lot.parking_price_vat_rate || "",
    // parking_count: lot.parking_count || "",
    renovation_price_excl_vat: lot.renovation_price_excl_vat || "",
    renovation_price_incl_tax: lot.renovation_price_incl_tax || "",
    renovation_price_vat_rate: lot.renovation_price_vat_rate || "",
  });

  const originalData = {
    reference: lot.reference || "",
    price_total: lot.price_total || "",
    rent: lot.rent || "",
    rent_profitability: lot.rent_profitability || "",
    commission: lot.commission || "",
    description: lot.description || "",
    price_excl_vat: lot.price_excl_vat || "",
    price_incl_tax: lot.price_incl_tax || "",
    price_vat_rate: lot.price_vat_rate || "",
    price_m2: lot.price_m2 || "",
    parking_price_excl_vat: lot.parking_price_excl_vat || "",
    parking_price_incl_tax: lot.parking_price_incl_tax || "",
    parking_price_vat_rate: lot.parking_price_vat_rate || "",
    // parking_count: lot.parking_count || "",
    renovation_price_excl_vat: lot.renovation_price_excl_vat || "",
    renovation_price_incl_tax: lot.renovation_price_incl_tax || "",
    renovation_price_vat_rate: lot.renovation_price_vat_rate || "",
  };

  const handleDescriptionChange = (html) => {
    setFormData((prev) => ({
      ...prev,
      description: html,
    }));
  };

  const hasFormChanges = () => {
    return Object.keys(formData).some(
      (key) => formData[key] !== originalData[key],
    );
  };

  const hasImageChanges = () => {
    return selectedImages.length > 0 || imagesToDelete.length > 0;
  };

  const isFormValid = hasFormChanges() || hasImageChanges();

  useEffect(() => {
    if (lot?.images && Array.isArray(lot.images)) {
      const allImages = lot.images.map((img) => ({
        id: img.id,
        url: imageLoader(img.image[0].url),
        name: img.name || img.alternativeText || "Image",
      }));
      setExistingImages(allImages);
    }
  }, [lot]);

  const handleDeleteExistingImage = (imageId) => {
    setShowDeleteConfirm(true);
    setImagesToDelete((prev) => [...prev, imageId]);
  };

  const confirmDelete = async () => {
    if (imagesToDelete.length === 0) return;

    try {
      const imageIdToDelete = imagesToDelete[0];
      const updatedImages = existingImages.filter(
        (img) => img.id !== imageIdToDelete,
      );
      const remainingImageIds = updatedImages.map((img) => img.id);

      await mainAxios.put(
        `${process.env.REACT_APP_ENDPOINT_URL}api/lots/${lot.documentId}`,
        {
          data: {
            images: remainingImageIds,
          },
        },
      );

      setExistingImages(updatedImages);
      dispatch(fetchSingleLot(lot.documentId));
      toast.success("Image supprimée du lot avec succès");
    } catch (error) {
      console.error("Error unlinking image from lot:", error);
      toast.error("Erreur lors de la suppression de l'image du lot");
    } finally {
      setShowDeleteConfirm(false);
      setImagesToDelete([]);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setImagesToDelete([]);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageUpload = async () => {
    if (selectedImages.length === 0) return [];

    const uploadedEntries = [];

    try {
      for (const file of selectedImages) {
        const reference = lot.reference || `lot-${lot.documentId}`;
        const entry = await uploadAndCreateImageEntry(file, reference);
        if (entry) {
          uploadedEntries.push(entry);
        }
      }

      setUploadedImageEntries(uploadedEntries);

      const newImages = uploadedEntries.map((entry) => ({
        id: entry.id,
        url: imageLoader(entry.origine_url),
        name: entry.name || entry.alternativeText || "Image",
      }));

      setExistingImages((prev) => [...prev, ...newImages]);

      toast.success(
        `${uploadedEntries.length} image(s) téléchargée(s) avec succès`,
      );

      setSelectedImages([]);

      return uploadedEntries;
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Erreur lors du téléchargement des images");
      return [];
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditLot = async () => {
    try {
      let imageEntries = uploadedImageEntries;
      if (selectedImages.length > 0 && uploadedImageEntries.length === 0) {
        imageEntries = await handleImageUpload();
      }

      const filteredData = Object.keys(formData).reduce((acc, key) => {
        if (
          formData[key] !== "" &&
          formData[key] !== null &&
          formData[key] !== undefined
        ) {
          acc[key] = formData[key];
        }
        return acc;
      }, {});

      const currentImageIds = existingImages.map((img) => img.id);
      const newImageIds = imageEntries.map((entry) => entry.id);
      const allImageIds = [...currentImageIds, ...newImageIds];

      if (allImageIds.length > 0) {
        filteredData.images = allImageIds;
      }

      const response = await mainAxios.put(
        `${process.env.REACT_APP_ENDPOINT_URL}api/lots/${lot.documentId}`,
        {
          data: {
            ...filteredData,
            isUpdated: true,
          },
        },
      );
      toast.success("Lot mis à jour avec succès");
      dispatch(fetchSingleLot(lot.documentId));
      dispatch(closeModal());
    } catch (error) {
      console.error("error", error.response?.data?.error?.name || error);
      toast.error("Echec de la mise à jour du lot");
    }
  };

  return (
    <div className="w100 d-flex flex-column">
      <div>
        <p className="semibold pink fs16">Fiche du lot</p>
        <hr />
      </div>
      <div className="d-flex gap40">
        {/* Single column for all fields */}
        <div className="w50 d-flex flex-column gap20">
          {/* Row: Reference / Prix */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Reference</p>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Prix</p>
              <input
                type="text"
                name="price_total"
                value={formData.price_total}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>

          {/* Row: Commission / Loyer */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Commission</p>
              <input
                type="text"
                name="commission"
                value={formData.commission}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Loyer</p>
              <input
                type="text"
                name="rent"
                value={formData.rent}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>

          {/* Row: Rentabilité / Prix HT */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Rentabilité</p>
              <input
                type="text"
                name="rent_profitability"
                value={formData.rent_profitability}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Prix HT</p>
              <input
                type="text"
                name="price_excl_vat"
                value={formData.price_excl_vat}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>

          {/* Row: Prix TTC / TVA (%) */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Prix TTC</p>
              <input
                type="text"
                name="price_incl_tax"
                value={formData.price_incl_tax}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">TVA (%)</p>
              <input
                type="text"
                name="price_vat_rate"
                value={formData.price_vat_rate}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>

          {/* Row: Prix au m² / Nombre de parkings */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Prix au m²</p>
              <input
                type="text"
                name="price_m2"
                value={formData.price_m2}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            {/* <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Nombre de parkings</p>
              <input
                type="text"
                name="parking_count"
                value={formData.parking_count}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div> */}
          </div>

          {/* Row: Prix parking HT / Prix parking TTC */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Prix parking HT</p>
              <input
                type="text"
                name="parking_price_excl_vat"
                value={formData.parking_price_excl_vat}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Prix parking TTC</p>
              <input
                type="text"
                name="parking_price_incl_tax"
                value={formData.parking_price_incl_tax}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>

          {/* Row: TVA parking / Rénovation HT */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">TVA parking (%)</p>
              <input
                type="text"
                name="parking_price_vat_rate"
                value={formData.parking_price_vat_rate}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Rénovation HT</p>
              <input
                type="text"
                name="renovation_price_excl_vat"
                value={formData.renovation_price_excl_vat}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>

          {/* Row: Rénovation TTC / TVA rénovation */}
          <div className="d-flex gap20">
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">Rénovation TTC</p>
              <input
                type="text"
                name="renovation_price_incl_tax"
                value={formData.renovation_price_incl_tax}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
            <div className="w50">
              <p className="medium darkGrey fs12 mb-10">TVA rénovation (%)</p>
              <input
                type="text"
                name="renovation_price_vat_rate"
                value={formData.renovation_price_vat_rate}
                onChange={handleChange}
                className="w100 customInput"
              />
            </div>
          </div>
        </div>

        <div className="w50 d-flex flex-column gap20">
          <div>
            <label>Ajouter des images</label>
            <button
              type="button"
              className="radius5 px-20 py-10 bg-pink text-white border-0 hoverOpacity"
              onClick={() =>
                document.getElementById("image-input-edit-lot").click()
              }
            >
              Sélectionner des images
            </button>
            <input
              id="image-input-edit-lot"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="d-none"
            />
          </div>
          {selectedImages.length > 0 && (
            <div className="selected-images d-flex flex-column gap10 mt-2">
              <p className="fw-bold mb-2">
                Images sélectionnées ({selectedImages.length}):
              </p>
              <div className="image-previews d-flex flex-wrap gap10">
                {selectedImages.map((file, index) => (
                  <div
                    key={index}
                    className="image-preview-item position-relative p-2 border rounded bg-light"
                  >
                    <p className="lot-image-name mb-0">{file.name}</p>
                    <button
                      type="button"
                      className="remove-image-btn position-absolute top-0 end-0 btn btn-sm text-danger"
                      onClick={() => removeImage(index)}
                      aria-label="Supprimer cette image"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {existingImages.length > 0 && (
            <div>
              <p>Images existantes:</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {existingImages.map((image, index) => (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "5px",
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                    <button
                      onClick={() => handleDeleteExistingImage(image.id)}
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        background: "red",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              minWidth: "300px",
              textAlign: "center",
            }}
          >
            <div style={{ marginBottom: "20px", fontSize: "16px" }}>
              Êtes-vous sûr de vouloir supprimer cette image du lot ?
            </div>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={confirmDelete}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Oui, supprimer
              </button>
              <button
                onClick={cancelDelete}
                style={{
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="my-4">
        <label htmlFor="description">Description</label>
        <TextHtmlEditor
          id="description"
          value={formData.description}
          onChange={handleDescriptionChange}
        />
      </div>

      <div className="d-flex justify-content-end mt-20">
        <button
          onClick={handleEditLot}
          disabled={!isFormValid}
          className={`radius5 px-20 py-1 white border-0 hoverOpacity ${
            isFormValid ? "bg-pink" : "bg-soft-grey"
          }`}
        >
          Appliquer
        </button>
      </div>
    </div>
  );
};

export default EditLotModal;
